import { useEffect, useState } from "react";
import { Briefcase, BookOpen, CalendarDays, ChevronDown, ClipboardList, Coins, FileText, FileDown, Gem, GraduationCap, LayoutDashboard, Lock, LogOut, Menu, NotebookPen, Package, ReceiptText, ScanLine, ShieldCheck, User, Users, Users2, WalletCards, X } from "lucide-react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import FloatingConta from "./FloatingConta";
import Logo from "./Logo";
import NotificationsBell from "./NotificationsBell";
import PaywallModal from "./PaywallModal";
import ProfileMenu from "./ProfileMenu";
import TrialBanner from "./TrialBanner";
import { useEntitlements } from "../lib/entitlements";
import { api, API_URL } from "../lib/api";

// [route, label, Icon, feature_key]
const links = [
  ["/", "Finanzas", LayoutDashboard, "finanzas"],
  ["/academia", "Academia", GraduationCap, "academia"],
  ["/tranquilo", "Calendario", CalendarDays, "calendario"],
  ["/ingresos", "Ingresos", WalletCards, "ingresos"],
  ["/gastos", "Egresos", ReceiptText, "egresos"],
  ["/caja", "Caja", Coins, "caja"],
  ["/proyectos", "Proyectos", Briefcase, "proyectos"],
  ["/importar", "Escanear", ScanLine, "escaner"],
  ["/declaraciones", "Declaración", ClipboardList, "declaraciones"],
  ["/clientes", "Clientes", Users, "clientes"],
  ["/proveedores", "Proveedores", Users, "proveedores"],
  ["/nomina", "Nómina", Users2, "nomina"],
  ["/boveda", "Mi Bóveda", ShieldCheck, "boveda"],
  ["/patrimonio", "Portafolio", Gem, "portafolio"],
  ["/notas", "Notas", NotebookPen, "notas"],
  ["/inventario", "Inventario", Package, "inventario"],
  ["/productos", "Productos", Package, "productos"],
  ["/facturas", "Facturas Enviadas", FileText, "facturas_enviadas"],
  ["/facturas-recibidas", "Facturas Recibidas", FileDown, "facturas_recibidas"],
  ["/perfil-financiero", "Perfil Financiero", WalletCards, "finanzas"],
];

// Split point: items BEFORE Facturación submenu button (positions 1-3)
const BEFORE_FACTURACION = 3;

function NavList({ open, setOpen, onNavigate }) {
  const { has, showUpgradeUI } = useEntitlements();
  // BUSINESS RULE (do not remove): while the 7-day trial is active we suppress
  // every Upgrade affordance in navigation (desktop sidebar + mobile drawer).
  // `showUpgradeUI` is the single source of truth — see /lib/entitlements.js
  // for the full rule and its auto-refresh behavior across trial expiry.
  const renderLink = ([to, label, Icon, feature]) => {
    const locked = feature && !has(feature);
    const showLockTag = locked && showUpgradeUI;
    return (
      <NavLink to={to} key={to} end={to === "/"} className={`sidebar-link${showLockTag ? " locked" : ""}`} onClick={onNavigate}>
        <Icon size={18} /><b>{label}</b>
        {showLockTag && <span className="sidebar-locked-tag" data-testid={`sidebar-upgrade-badge-${feature}`}><Lock size={9} /> Upgrade</span>}
      </NavLink>
    );
  };
  return (
    <nav className="sidebar-nav">
      {links.slice(0, BEFORE_FACTURACION).map(renderLink)}
      <button className="sidebar-link sidebar-expand" onClick={() => setOpen(!open)} data-testid="sidebar-facturacion-toggle">
        <FileText size={18} />Facturación<ChevronDown size={15} className={open ? "rotate" : ""} />
      </button>
      {open && (
        <div className="invoice-submenu">
          <NavLink to="/facturacion" onClick={onNavigate}>Crear factura</NavLink>
          <NavLink to="/facturas" onClick={onNavigate}>Enviadas</NavLink>
          <NavLink to="/facturas?tipo=recibidas" onClick={onNavigate}>Recibidas</NavLink>
        </div>
      )}
      {links.slice(BEFORE_FACTURACION).map(renderLink)}
    </nav>
  );
}

export default function AppShell({ children }) {
  const [invoiceOpen, setInvoiceOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [account, setAccount] = useState(null);
  const [avatarUrl, setAvatarUrl] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  // Load account for drawer profile block
  useEffect(() => {
    api.get("/account/me", { withCredentials: true }).then((r) => setAccount(r.data)).catch(() => setAccount(null));
  }, []);

  useEffect(() => {
    if (!account) return;
    if (account.avatar_uploaded) {
      const token = window.localStorage.getItem("session_token");
      fetch(`${API_URL}/account/avatar`, { credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {} })
        .then((r) => (r.ok ? r.blob() : null))
        .then((blob) => blob && setAvatarUrl(URL.createObjectURL(blob)))
        .catch(() => setAvatarUrl(""));
    } else if (account.picture) {
      setAvatarUrl(account.picture);
    } else {
      setAvatarUrl("");
    }
  }, [account?.avatar_uploaded, account?.picture]);

  // Close drawer on route change
  useEffect(() => { setDrawerOpen(false); }, [location.pathname]);

  // Escape to close + body scroll lock
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") setDrawerOpen(false); };
    window.addEventListener("keydown", onKey);
    if (drawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [drawerOpen]);

  const logout = async () => {
    try { await api.post("/auth/logout", {}, { withCredentials: true }); } catch (_) { /* noop */ }
    window.localStorage.removeItem("session_token");
    setDrawerOpen(false);
    navigate("/", { replace: true });
    window.location.reload();
  };

  const initials = (account?.name || account?.email || "?").split(" ").map((n) => n[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="sidebar-shell">
      {/* Desktop sidebar */}
      <aside className="sidebar" data-testid="main-navigation">
        <Link to="/" className="sidebar-brand" data-testid="sidebar-brand">
          <Logo variant="full" size="lg" testid="sidebar-brand-logo" />
        </Link>
        <NavList open={invoiceOpen} setOpen={setInvoiceOpen} />
      </aside>

      {/* Mobile header (fixed) */}
      <header className="mobile-header" data-testid="mobile-header">
        <Link to="/" className="mobile-header-brand" data-testid="mobile-header-brand">
          <Logo variant="full" size="md" testid="mobile-header-logo" />
        </Link>
        <div className="mobile-header-actions">
          <button
            type="button"
            className="mobile-header-menu"
            onClick={() => setDrawerOpen(true)}
            aria-label="Abrir menú"
            data-testid="mobile-hamburger"
          ><Menu size={22} /></button>
          <NotificationsBell />
          <ProfileMenu compact />
        </div>
        <TrialBanner />
      </header>

      {/* Mobile drawer */}
      <div
        className={`mobile-drawer-overlay ${drawerOpen ? "open" : ""}`}
        onClick={() => setDrawerOpen(false)}
        data-testid="mobile-drawer-overlay"
        aria-hidden={!drawerOpen}
      />
      <aside
        className={`mobile-drawer ${drawerOpen ? "open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-hidden={!drawerOpen}
        data-testid="mobile-drawer"
      >
        <div className="mobile-drawer-head">
          <Link to="/" className="sidebar-brand" onClick={() => setDrawerOpen(false)} data-testid="drawer-brand">
            <Logo variant="full" size="md" testid="drawer-brand-logo" />
          </Link>
          <button type="button" className="mobile-drawer-close" onClick={() => setDrawerOpen(false)} aria-label="Cerrar" data-testid="drawer-close">
            <X size={20} />
          </button>
        </div>

        <button
          type="button"
          className="mobile-drawer-profile"
          onClick={() => { setDrawerOpen(false); navigate("/mi-perfil"); }}
          data-testid="drawer-profile"
        >
          <span className="mobile-drawer-avatar">
            {avatarUrl ? <img src={avatarUrl} alt="" /> : <b>{initials || <User size={18} />}</b>}
          </span>
          <span className="mobile-drawer-profile-info">
            <b>{account?.name || "Cuenta"}</b>
            <small>{account?.email || "Iniciar sesión"}</small>
          </span>
        </button>

        <NavList open={invoiceOpen} setOpen={setInvoiceOpen} onNavigate={() => setDrawerOpen(false)} />

        <button
          type="button"
          className="mobile-drawer-logout"
          onClick={logout}
          data-testid="drawer-logout"
        >
          <LogOut size={16} /> Cerrar sesión
        </button>
      </aside>

      <main className="sidebar-main">
        <header className="workspace-topbar" data-testid="workspace-topbar">
          <TrialBanner />
          <NotificationsBell />
          <ProfileMenu />
        </header>
        {children}
      </main>
      <FloatingConta />
      <PaywallModal />
    </div>
  );
}
